<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type {
  AppointmentConfirmationResult,
  PublicAvailabilitySlot,
  PublicService,
  TemporaryReservationResult,
} from '@terapeut-ia/shared-types'
import {
  confirmAppointment,
  createTemporaryReservation,
  getAvailability,
  releaseTemporaryReservation,
} from '../services/api'

const props = defineProps<{ service: PublicService }>()
const emit = defineEmits<{ close: []; reserved: [conversationId: string] }>()

const date = ref(new Date().toLocaleDateString('en-CA'))
const slots = ref<PublicAvailabilitySlot[]>([])
const loading = ref(false)
const reserving = ref<number | null>(null)
const confirming = ref(false)
const error = ref<string | null>(null)
const message = ref<string | null>(null)
const reservation = ref<TemporaryReservationResult | null>(null)
const confirmation = ref<AppointmentConfirmationResult | null>(null)
const clientName = ref('')
const phone = ref('')
const remainingSeconds = ref(0)
const confirmationKey = ref(sessionStorage.getItem('appointment_confirmation_key') ?? crypto.randomUUID())
let intervalId: number | undefined

const maxDate = computed(() => {
  const value = new Date()
  value.setDate(value.getDate() + 60)
  return value.toLocaleDateString('en-CA')
})

const countdown = computed(() => {
  const minutes = Math.floor(remainingSeconds.value / 60).toString().padStart(2, '0')
  const seconds = (remainingSeconds.value % 60).toString().padStart(2, '0')
  return `${minutes}:${seconds}`
})

function formatPrice(cents: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100)
}

function formatDateTime(epoch: number, timezone: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone: timezone,
  }).format(new Date(epoch * 1000))
}

function updateCountdown(): void {
  if (!reservation.value) return
  remainingSeconds.value = Math.max(0, reservation.value.expiresAt - Math.floor(Date.now() / 1000))
  if (remainingSeconds.value === 0) {
    reservation.value = null
    sessionStorage.removeItem('temporary_reservation')
    sessionStorage.removeItem('appointment_confirmation_key')
    confirmationKey.value = crypto.randomUUID()
    error.value = 'O tempo da reserva terminou. Escolha o horário novamente.'
    void loadSlots()
  }
}

async function loadSlots(): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const response = await getAvailability(props.service.id, date.value)
    slots.value = response.slots
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Falha ao consultar horários.'
    slots.value = []
  } finally {
    loading.value = false
  }
}

async function reserve(slot: PublicAvailabilitySlot): Promise<void> {
  reserving.value = slot.startAt
  error.value = null
  message.value = null
  try {
    const response = await createTemporaryReservation({
      serviceId: props.service.id,
      startAt: slot.startAt,
      conversationId: sessionStorage.getItem('conversation_id') ?? undefined,
      idempotencyKey: crypto.randomUUID(),
    })
    reservation.value = response
    confirmationKey.value = crypto.randomUUID()
    sessionStorage.setItem('conversation_id', response.conversationId)
    sessionStorage.setItem('temporary_reservation', JSON.stringify(response))
    sessionStorage.setItem('appointment_confirmation_key', confirmationKey.value)
    emit('reserved', response.conversationId)
    updateCountdown()
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Não foi possível reservar este horário.'
    await loadSlots()
  } finally {
    reserving.value = null
  }
}

async function confirm(): Promise<void> {
  if (!reservation.value) return
  if (clientName.value.trim().length < 2) {
    error.value = 'Informe seu nome.'
    return
  }
  if (phone.value.replace(/\D/g, '').length < 10) {
    error.value = 'Informe um telefone válido com DDD.'
    return
  }

  confirming.value = true
  error.value = null
  try {
    const result = await confirmAppointment({
      reservationId: reservation.value.id,
      conversationId: reservation.value.conversationId,
      clientName: clientName.value,
      phone: phone.value,
      idempotencyKey: confirmationKey.value,
    })
    confirmation.value = result
    reservation.value = null
    sessionStorage.removeItem('temporary_reservation')
    sessionStorage.removeItem('appointment_confirmation_key')
    message.value = result.status === 'CONFIRMED'
      ? 'Agendamento confirmado.'
      : 'Solicitação registrada e aguardando confirmação.'
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Não foi possível confirmar o agendamento.'
  } finally {
    confirming.value = false
  }
}

async function release(): Promise<void> {
  if (!reservation.value) return
  const current = reservation.value
  try {
    await releaseTemporaryReservation(current.id, current.conversationId)
  } finally {
    reservation.value = null
    sessionStorage.removeItem('temporary_reservation')
    sessionStorage.removeItem('appointment_confirmation_key')
    confirmationKey.value = crypto.randomUUID()
    await loadSlots()
  }
}

async function copyCode(): Promise<void> {
  if (!confirmation.value) return
  await navigator.clipboard.writeText(confirmation.value.managementCode)
  message.value = 'Código copiado.'
}

watch(date, loadSlots)
onMounted(() => {
  const saved = sessionStorage.getItem('temporary_reservation')
  if (saved) {
    try {
      const parsed = JSON.parse(saved) as TemporaryReservationResult
      if (parsed.serviceId === props.service.id && parsed.expiresAt > Math.floor(Date.now() / 1000)) {
        reservation.value = parsed
      }
    } catch {
      sessionStorage.removeItem('temporary_reservation')
    }
  }
  void loadSlots()
  intervalId = window.setInterval(updateCountdown, 1000)
  updateCountdown()
})
onBeforeUnmount(() => {
  if (intervalId) window.clearInterval(intervalId)
})
</script>

<template>
  <div class="booking-backdrop" @click.self="emit('close')">
    <section class="booking-card">
      <div class="flex items-start justify-between gap-4">
        <div>
          <p class="section-label">Agendamento</p>
          <h2 class="mt-2 font-display text-3xl">{{ service.name }}</h2>
          <p class="mt-2 text-sm text-white/45">{{ service.durationMinutes }} minutos · {{ formatPrice(service.priceCents) }}</p>
        </div>
        <button class="booking-close" type="button" @click="emit('close')">×</button>
      </div>

      <div v-if="error" class="mt-5 rounded-2xl border border-red-400/20 bg-red-500/10 p-4 text-sm text-red-100">{{ error }}</div>
      <div v-if="message" class="mt-5 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 p-4 text-sm text-emerald-100">{{ message }}</div>

      <div v-if="confirmation" class="mt-7 rounded-3xl border border-[#c95a7a]/35 bg-[#8c1838]/15 p-6">
        <p class="section-label">{{ confirmation.status === 'CONFIRMED' ? 'Confirmado' : 'Aguardando confirmação' }}</p>
        <h3 class="mt-3 font-display text-2xl">{{ formatDateTime(confirmation.scheduledStartAt, confirmation.timezone) }}</h3>
        <p class="mt-3 text-sm text-white/60">{{ confirmation.clientName }} · {{ confirmation.serviceName }}</p>
        <div class="mt-6 rounded-2xl border border-white/10 bg-black/20 p-5 text-center">
          <p class="text-xs uppercase tracking-[.2em] text-white/40">Código para consultar, reagendar ou cancelar</p>
          <strong class="mt-3 block font-mono text-3xl tracking-[.15em] text-[#f2a3bb]">{{ confirmation.managementCode }}</strong>
          <p class="mt-3 text-xs leading-5 text-white/40">Guarde este código junto com o telefone informado.</p>
          <button class="secondary-button mt-4" type="button" @click="copyCode">Copiar código</button>
        </div>
        <button class="primary-button mt-6 w-full" type="button" @click="emit('close')">Concluir</button>
      </div>

      <div v-else-if="reservation" class="mt-7 rounded-3xl border border-[#c95a7a]/35 bg-[#8c1838]/15 p-6">
        <div class="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p class="section-label">Horário protegido</p>
            <h3 class="mt-3 font-display text-2xl">{{ formatDateTime(reservation.startAt, reservation.timezone) }}</h3>
          </div>
          <strong class="font-mono text-xl text-[#f0a0b8]">{{ countdown }}</strong>
        </div>
        <p class="mt-4 text-sm leading-6 text-white/60">Informe nome e telefone para transformar a reserva temporária em agendamento.</p>
        <div class="mt-5 grid gap-4">
          <label>
            <span class="text-xs text-white/50">Nome</span>
            <input v-model="clientName" class="booking-field mt-2" autocomplete="name" maxlength="120" placeholder="Seu nome" type="text">
          </label>
          <label>
            <span class="text-xs text-white/50">Telefone com DDD</span>
            <input v-model="phone" class="booking-field mt-2" autocomplete="tel" inputmode="tel" maxlength="30" placeholder="(41) 99999-9999" type="tel">
          </label>
        </div>
        <div class="mt-5 flex flex-wrap gap-3">
          <button class="primary-button flex-1" type="button" :disabled="confirming" @click="confirm">
            {{ confirming ? 'Confirmando...' : service.confirmationMode === 'AUTOMATIC' ? 'Confirmar agendamento' : 'Enviar solicitação' }}
          </button>
          <button class="secondary-button" type="button" :disabled="confirming" @click="release">Liberar horário</button>
        </div>
      </div>

      <template v-else>
        <label class="mt-7 block">
          <span class="text-xs text-white/50">Data</span>
          <input v-model="date" class="booking-field mt-2" type="date" :min="new Date().toLocaleDateString('en-CA')" :max="maxDate">
        </label>
        <div v-if="loading" class="mt-6 py-10 text-center text-sm text-white/40">Consultando agenda...</div>
        <div v-else-if="slots.length" class="mt-6 grid grid-cols-3 gap-3 sm:grid-cols-4">
          <button
            v-for="slot in slots"
            :key="slot.startAt"
            class="slot-button"
            type="button"
            :disabled="reserving !== null"
            @click="reserve(slot)"
          >
            {{ reserving === slot.startAt ? '...' : slot.localTime }}
          </button>
        </div>
        <div v-else class="mt-6 rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-white/40">Nenhum horário disponível nesta data.</div>
      </template>
    </section>
  </div>
</template>
